import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import {
  Control,
  FieldValues,
  Path,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';

import AutocompleteLabelsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteLabelsTypeahead';
import AutocompleteMessagingRecipientsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteMessagingRecipientsTypeahead';
import AutocompleteVendorCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteVendorCompaniesTypeahead';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import {
  fetchMatchingRecipients,
  MAX_BROADCAST_RECIPIENTS,
  mergeMatchingRecipients,
  messagingRecipientsQuery,
} from '~hooks/useMessaging/messagingRecipients';
import reportRequestError from '~services/reportRequestError';
import { LabeledOption } from '~types/LabeledOption';
import { ActingCompany } from '~utils/vendorCompanyOptions';

type RecipientFilterValues = {
  labels: LabeledOption[];
  vendors: LabeledOption[];
};

const emptyRecipientFilters: RecipientFilterValues = {
  labels: [],
  vendors: [],
};

type BroadcastRecipientsFieldProps<T extends FieldValues> = {
  actingCompany: ActingCompany;
  canFilterByLabel: boolean;
  control: Control<T>;
  name: Path<T>;
  unreachableRecipientIds?: string[];
};

export default function BroadcastRecipientsField<T extends FieldValues>({
  actingCompany,
  canFilterByLabel,
  control,
  name,
  unreachableRecipientIds,
}: BroadcastRecipientsFieldProps<T>) {
  const theme = useTheme();
  const { field } = useController({ control, name });
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [isCapped, setIsCapped] = useState(false);
  const [search, setSearch] = useState('');
  const issueSelectAllSignal = useLatestRequestSignal();

  const { control: filtersControl } = useForm<RecipientFilterValues>({
    defaultValues: emptyRecipientFilters,
  });
  const clearSelection = field.onChange;
  const unreachableRecipientIdSet = useMemo(
    () => new Set(unreachableRecipientIds),
    [unreachableRecipientIds],
  );
  const isUnreachable = (option: LabeledOption) =>
    unreachableRecipientIdSet.has(option.value);

  const resetSelectionForNewFilters = useCallback(() => {
    // A fresh signal aborts a select all still loading against the old filters.
    issueSelectAllSignal();
    setIsSelectingAll(false);
    clearSelection([]);
    setIsCapped(false);
  }, [issueSelectAllSignal, clearSelection]);

  const [vendors, labels] = useWatch({
    control: filtersControl,
    name: ['vendors', 'labels'],
  });

  const recipientFilters = useMemo(
    () => ({
      companyIds: vendors.map((vendor) => vendor.value),
      labelIds: labels.map((label) => label.value),
      scopedCompanyId: actingCompany.id,
    }),
    [actingCompany.id, vendors, labels],
  );
  const narrowsMatches =
    vendors.length > 0 || labels.length > 0 || search.trim().length > 0;

  const recipientsQuery = useMemo(
    () => messagingRecipientsQuery(recipientFilters),
    [recipientFilters],
  );

  const selected = (field.value ?? []) as LabeledOption[];
  const selectedCount = selected.length;
  const selectedUnreachableRecipients = selected.filter(isUnreachable);

  const selectAllMatching = useCallback(async () => {
    const signal = issueSelectAllSignal();
    setIsSelectingAll(true);

    try {
      const matching = await fetchMatchingRecipients(
        { ...recipientFilters, search: search.trim() },
        signal,
      );
      if (signal.aborted) return;

      const merged = mergeMatchingRecipients(
        selected,
        matching.recipients,
        matching.hasMore,
      );

      field.onChange(merged.recipients);
      setIsCapped(merged.isCapped);
    } catch (error) {
      if (signal.aborted) return;

      reportRequestError(error, t('error_messages.messaging.failed_to_load_drivers'));
    } finally {
      if (!signal.aborted) {
        setIsSelectingAll(false);
      }
    }
  }, [issueSelectAllSignal, recipientFilters, search, selected, field.onChange]);

  // Memoized so typing a search doesn't re-render the filter typeaheads.
  const filterFields = useMemo(
    () => (
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <AutocompleteVendorCompaniesTypeahead
          control={filtersControl}
          isMultiple
          label={t('messaging.recipient_filter_vendors')}
          name="vendors"
          onValueChange={resetSelectionForNewFilters}
          ownCompany={actingCompany}
        />

        {canFilterByLabel && (
          <AutocompleteLabelsTypeahead
            companyId={actingCompany.id}
            control={filtersControl}
            isMultiple
            label={t('messaging.recipient_filter_labels')}
            name="labels"
            onValueChange={resetSelectionForNewFilters}
          />
        )}
      </Box>
    ),
    [actingCompany, canFilterByLabel, filtersControl, resetSelectionForNewFilters],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {filterFields}

      <AutocompleteMessagingRecipientsTypeahead
        control={control}
        isDisabled={isSelectingAll}
        isMultiple
        isRequired
        label={t('messaging.recipients')}
        isOptionInvalid={isUnreachable}
        name={name}
        onSearchChange={setSearch}
        ownCompanyId={actingCompany.id}
        recipientFilters={recipientsQuery}
        search={search}
      />

      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
        <SmallLoadingButton
          color="secondary"
          loading={isSelectingAll}
          onClick={() => void selectAllMatching()}
          variant="text"
        >
          {narrowsMatches
            ? t('messaging.select_all_matching')
            : t('messaging.select_all')}
        </SmallLoadingButton>

        {selectedCount > 0 && (
          <Typography sx={{ color: theme.semantic.textSecondary }} variant="caption">
            {t('messaging.recipients_selected', { count: selectedCount })}
          </Typography>
        )}
      </Box>

      {isCapped && selectedCount === MAX_BROADCAST_RECIPIENTS && (
        <Typography sx={{ color: 'warning.main' }} variant="caption">
          {t('messaging.recipients_capped', { count: MAX_BROADCAST_RECIPIENTS })}
        </Typography>
      )}

      {selectedUnreachableRecipients.length > 0 && (
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ color: theme.semantic.errorText }} variant="caption">
            {t('messaging.recipients_unreachable', {
              count: selectedUnreachableRecipients.length,
              names: selectedUnreachableRecipients
                .map((option) => option.label)
                .join(', '),
            })}
          </Typography>

          <SmallButton
            color="secondary"
            onClick={() =>
              field.onChange(selected.filter((option) => !isUnreachable(option)))
            }
            variant="text"
          >
            {t('messaging.remove_unreachable_recipients')}
          </SmallButton>
        </Box>
      )}

      {selectedCount > MAX_BROADCAST_RECIPIENTS && (
        <Typography sx={{ color: theme.semantic.errorText }} variant="caption">
          {t('messaging.recipients_over_limit', { count: MAX_BROADCAST_RECIPIENTS })}
        </Typography>
      )}
    </Box>
  );
}
